import { useQuery } from '@tanstack/react-query';
import { EmptyState } from '../../components';
import { formatDateTime } from '../../lib/format';
import { useRequiredWorkspace } from '../workspace/useWorkspace';
import { fetchRequestActivity } from './api';
import { activityQueryKeys } from './queryKeys';
import { formatActivity } from './formatActivity';

/**
 * The Activity tab on one request — self-contained: give it a `requestId`
 * and it fetches and renders its own trail. `RequestDetailPage.tsx` (owned by
 * another agent) is expected to mount this behind a tab, not to reach into it.
 *
 * The server has already applied the client/internal split (see
 * activity.service.ts `listForRequest`) and already 404s the whole thing if
 * the caller cannot see the request at all, so nothing here re-checks
 * visibility — there is no `visibleToClient` on `ActivityRow` to check,
 * because a row that failed that test never arrives in the response.
 */
export interface RequestActivityTabProps {
  readonly requestId: string;
}

const LIMIT = 50;

export function RequestActivityTab({ requestId }: RequestActivityTabProps) {
  const { workspace } = useRequiredWorkspace();

  const query = useQuery({
    queryKey: activityQueryKeys.request(workspace.id, requestId),
    queryFn: ({ signal }) => fetchRequestActivity(workspace.id, requestId, { limit: LIMIT }, signal),
  });

  const items = query.data?.items ?? [];

  if (query.isPending) {
    return <p className="px-3 py-6 fs-12 text-muted">Loading activity…</p>;
  }
  if (query.error !== null) {
    return <EmptyState title="Could not load activity" description={query.error.message} />;
  }
  if (items.length === 0) {
    return (
      <EmptyState title="Nothing yet" description="Changes to this request show up here as they happen." />
    );
  }

  return (
    <ol className="flex flex-col gap-3">
      {items.map((row) => (
        <li key={row.id} className="flex gap-2.5 fs-12.5">
          <span className="mt-[0.1875rem] size-1.5 flex-none rounded-full bg-line-strong" aria-hidden />
          <span className="min-w-0 flex-1">
            <span className="text-ink-soft">{formatActivity(row)}</span>
            <span className="ml-1.5 whitespace-nowrap fs-11 text-faint">{formatDateTime(row.createdAt)}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}
